import React from "react";
import GemBadge from "./GemBadge";
import useReputation from "../hooks/useReputation";

const STAFF_ROLE_LABEL = {
  ceo: "CEO",
  master_admin: "CEO",
  senior_admin: "Senior",
  admin: "Admin",
  moderator: "Mod",
  support: "Support",
};

/**
 * UserGem — inline gem badge + optional cosmetic title pill + optional Staff
 * pill next to a user name.
 *
 * Staff users get a small gold/cyan "STAFF · {Role}" pill rendered AFTER the
 * gem and BEFORE the cosmetic title. The pill is always visible (cannot be
 * suppressed) since it represents Hidden Gem identity, not a cosmetic.
 *
 * Props:
 *   userId: string (required)
 *   size: 'xs' | 'sm' | 'md' | 'lg'
 *   showLabel: bool — tier label pill next to the gem
 *   showTitle: bool (default true) — equipped cosmetic title pill
 *   fallbackTier: optional tier key to render immediately while data loads
 */
export default function UserGem({ userId, size = "sm", showLabel = false, showTitle = true, fallbackTier }) {
  const rep = useReputation(userId);
  const tier = rep?.tier || fallbackTier || "white";
  if (!userId) return null;
  const title = rep?.cosmetics?.title;
  const variant = rep?.cosmetics?.gem_variant;
  const isStaff = !!rep?.is_staff;
  const staffLabel = STAFF_ROLE_LABEL[rep?.staff_role] || (isStaff ? "Staff" : null);
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      <GemBadge
        tier={tier}
        size={size}
        showLabel={showLabel}
        variant={variant}
        title={rep ? `${rep.tier_name} · ${rep.tier_label}${variant?.label ? ` · ${variant.label}` : ""}` : undefined}
      />
      {isStaff && (
        <span
          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-black inline-flex items-center gap-0.5 bg-gradient-to-r from-amber-400/25 to-cyan-400/25 text-amber-200 border border-amber-400/50"
          data-testid={`user-staff-pill-${rep?.staff_role || "staff"}`}
          title="Hidden Gem Staff"
        >
          <svg viewBox="0 0 24 24" className="w-2.5 h-2.5" fill="currentColor" aria-hidden="true">
            <path d="M3 7l4 4 5-7 5 7 4-4-2 12H5L3 7z" />
          </svg>
          {staffLabel}
        </span>
      )}
      {showTitle && title && (
        <span
          className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full font-black border whitespace-nowrap"
          style={{ color: title.color, borderColor: `${title.color}66`, background: `${title.color}1a` }}
          data-testid={`user-title-${title.id}`}
        >
          {title.label}
        </span>
      )}
    </span>
  );
}
